import { useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import {
  DrawerContent,
  DrawerFooter,
  DrawerTopBar,
  DrawerTopBarActionButton,
  SwipeableDrawer,
} from "@/components/Drawer";
import { StarRatingFilter } from "@/features/hotels/components/hotels-list/StarRatingFilter";
import { HOTEL_CLASSES } from "@/features/hotels/utils/constants";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";
import { rudderAnalytics, HotelFiltersTrackingEvents } from "@/lib/rudderstack";

type StarRatingFilterContentsProps = {
  defaultFilter?: number[];
  value?: number[];
  onSubmit: (newValue: number[]) => void;
  onCloseClick: () => void;
};

export const StarRatingFilterContents = ({
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: StarRatingFilterContentsProps) => {
  const intl = useIntl();

  const [localFilter, setLocalFilter] = useState(value);

  const { toggle } = useToggleSelectAll<number>({
    selected: localFilter,
    all: HOTEL_CLASSES,
  });

  return (
    <>
      <DrawerTopBar
        onCloseClick={onCloseClick}
        title={intl.formatMessage({
          defaultMessage: "Star rating",
          description: "Filter section label for filtering by hotel star class",
        })}
        actionElement={
          <DrawerTopBarActionButton
            onClick={() => {
              setLocalFilter(defaultFilter);
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Clear filter",
              description: "Button to remove a single active filter",
            })}
          </DrawerTopBarActionButton>
        }
      />

      <DrawerContent>
        <StarRatingFilter
          selectedClasses={localFilter}
          onChange={(_e, checked, option) => {
            setLocalFilter((prevFilter) => {
              const newClasses = toggle(prevFilter, option, checked);
              return newClasses.sort((a, b) => a - b);
            });
          }}
        />
      </DrawerContent>

      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => {
            rudderAnalytics.track(
              HotelFiltersTrackingEvents.USER_CLICKED_ON_APPLY_STAR,
              {
                stars_selected: localFilter.filter((s) => s !== 0),
                include_unrated_hotels: localFilter.includes(0),
              },
            );
            onSubmit(localFilter);
            onCloseClick();
          }}
          variant="contained"
          size="large"
        >
          {intl.formatMessage({
            defaultMessage: "Apply",
            description: "Button to apply selected filters or changes",
          })}
        </Button>
      </DrawerFooter>
    </>
  );
};

type Props = StarRatingFilterContentsProps & {
  open: boolean;
};

export const StarRatingFilterDrawer = ({
  open,
  defaultFilter = [],
  value = [],
  onSubmit,
  onCloseClick,
}: Props) => {
  return (
    <SwipeableDrawer open={open} onClose={onCloseClick}>
      <StarRatingFilterContents
        defaultFilter={defaultFilter}
        value={value}
        onSubmit={onSubmit}
        onCloseClick={onCloseClick}
      />
    </SwipeableDrawer>
  );
};
